import { useEffect, useRef, useState } from "react";
import { SerializedStyles, css } from "@emotion/react";

interface OtpInputProps {
  amount: number;
  autoFocus?: boolean;
  handleOutputString: (output: string) => void;
  inputRegExp: RegExp;
}

const OtpInput = ({
  amount,
  autoFocus,
  handleOutputString,
  inputRegExp,
}: OtpInputProps) => {
  const [inputValues, setInputValues] = useState<string[]>(
    Array(amount).fill("")
  );

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const inputRefs = Array.from({ length: amount }, () =>
    useRef<HTMLInputElement | null>(null)
  );

  useEffect(() => {
    const handleBackspace = (e: KeyboardEvent) => {
      if (e.key === "Backspace") {
        e.preventDefault();
        const currentIndex = inputRefs.findIndex(
          (ref) => ref?.current === document.activeElement
        );
        if (currentIndex >= 0) {
          const newInputValues = [...inputValues];
          const currentInputValue = newInputValues[currentIndex];
          if (currentInputValue.length > 0) {
            newInputValues[currentIndex] = currentInputValue.slice(
              0,
              currentInputValue.length - 1
            );
          } else if (currentIndex > 0) {
            newInputValues[currentIndex - 1] = "";
            inputRefs[currentIndex - 1]?.current?.focus();
          }
          setInputValues(newInputValues);
          handleOutputString(newInputValues.join(""));
        }
      }
    };

    const handlePaste = (e: ClipboardEvent) => {
      e.preventDefault();

      if (e.clipboardData) {
        const pastedText = e.clipboardData.getData("text/plain");

        if (pastedText.length === amount) {
          const newInputValues = [...pastedText.split("")];
          setInputValues(newInputValues);
          handleOutputString(newInputValues.join(""));
          inputRefs[0]?.current?.focus();
        }
      }
    };

    window.addEventListener("keydown", handleBackspace);
    window.addEventListener("paste", handlePaste as EventListener);

    return () => {
      window.removeEventListener("keydown", handleBackspace);
      window.removeEventListener("paste", handlePaste as EventListener);
    };
  }, [inputValues, inputRefs, handleOutputString, amount]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const inputValue = e.target.value;

    if (inputRegExp.test(inputValue)) {
      const newInputValues = [...inputValues];
      newInputValues[index] = inputValue;
      setInputValues(newInputValues);
      handleOutputString(newInputValues.join(""));

      if (index < amount - 1 && inputValue.length === 1) {
        inputRefs[index + 1]?.current?.focus();
      }
    }
  };

  return (
    <div className="otp-input-container" >
      {inputValues.map((value, index) => (
        <input
          key={index}
          className="otp-input"
          type="text"
          autoFocus={autoFocus && index === 0}
          value={value}
          onChange={(e) => handleInputChange(e, index)}
          ref={inputRefs[index]}
        />
      ))}
    </div>
  );
};

export default OtpInput;
